import { LazyQueryResultTuple, OperationVariables } from "@apollo/client";
import { useCallback, useEffect, useMemo } from "react";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";

type UseFetchOptionsOptions<
  Option,
  TData extends { [key: string]: unknown },
  TVariables extends OperationVariables,
> = {
  getOptions: (data: TData) => Option[] | readonly Option[] | null | undefined;
  lazyQuery: LazyQueryResultTuple<TData, TVariables>;
  menuIsOpen?: boolean;
  skip?: boolean;
};

export default function useFetchOptions<
  Option,
  TData extends { [key: string]: unknown },
  TVariables extends OperationVariables,
>({
  getOptions,
  lazyQuery,
  menuIsOpen,
  skip,
}: UseFetchOptionsOptions<Option, TData, TVariables>) {
  const [fetchOptions, { data, loading: isLoading, called: isCalled }] =
    lazyQuery;

  const { showErrorToast } = useAsyncCompleteToast();

  const performFetch = useCallback(() => {
    if (skip) return;
    fetchOptions().catch((reason) => {
      showErrorToast(reason, {
        title: "An error occurred getting menu options",
      });
    });
  }, [skip, fetchOptions, showErrorToast]);

  useEffect(() => {
    if (menuIsOpen && !isCalled) performFetch();
  }, [menuIsOpen, isCalled, performFetch]);

  const options = useMemo(
    () => (data && getOptions(data)) || undefined,
    [getOptions, data],
  );

  return {
    isLoading,
    onMenuOpen: performFetch,
    options,
  };
}
